import React, { useEffect, useState } from "react";
import { api, apiErrorMessage, formatCurrency, formatDate } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import {
    Coins, Package, Tag, Stack, WarningCircle, WarningOctagon, Receipt, TrendUp,
    ClipboardText, CheckCircle, Users, Storefront, ChartLineUp, ShoppingBag,
    Wallet, WhatsappLogo,
} from "@phosphor-icons/react";
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area,
} from "recharts";
import { toast } from "sonner";

const CHART_COLORS = ["#15803D", "#F59E0B", "#0A0A0A", "#DC2626", "#0EA5E9", "#8B5CF6", "#EC4899"];

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [payStats, setPayStats] = useState(null);
    const [charts, setCharts] = useState(null);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const [s, ps, c, a] = await Promise.all([
                api.get("/dashboard/stats"),
                api.get("/dashboard/payment-stats"),
                api.get("/dashboard/charts"),
                api.get("/dashboard/activity?limit=10"),
            ]);
            setStats(s.data);
            setPayStats(ps.data);
            setCharts(c.data);
            setActivity(a.data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 30000); // auto refresh every 30s
        return () => clearInterval(t);
    }, []);

    if (loading || !stats || !charts) {
        return <div className="text-sm text-neutral-500" data-testid="dashboard-loading">Loading dashboard...</div>;
    }

    const sparkFromMonthly = charts.monthly_sales.slice(-8);
    const sparkPurchase = charts.monthly_purchase.slice(-8);
    const sparkProfit = charts.monthly_profit.slice(-8);

    const monthlyData = charts.monthly_labels.map((label, i) => ({
        month: label,
        sales: charts.monthly_sales[i],
        purchase: charts.monthly_purchase[i],
        profit: charts.monthly_profit[i],
    }));

    const kpis = [
        { label: "Total Stock Value", value: stats.total_stock_value, format: formatCurrency, Icon: Coins, spark: sparkFromMonthly, testId: "kpi-stock-value" },
        { label: "Total Products", value: stats.total_products, Icon: Package, spark: sparkFromMonthly, testId: "kpi-products" },
        { label: "Total Categories", value: stats.total_categories, Icon: Tag, testId: "kpi-categories" },
        { label: "Total Stock Units", value: stats.total_stock_units, Icon: Stack, testId: "kpi-stock-units" },
        { label: "Low Stock Products", value: stats.low_stock_count, Icon: WarningCircle, accent: "#F59E0B", testId: "kpi-low-stock" },
        { label: "Out of Stock", value: stats.out_of_stock_count, Icon: WarningOctagon, accent: "#DC2626", testId: "kpi-out-of-stock" },
        { label: "Today's Sales", value: stats.today_sales, change: stats.today_sales_change, format: formatCurrency, Icon: Receipt, spark: sparkFromMonthly, testId: "kpi-today-sales" },
        { label: "Monthly Sales", value: stats.month_sales, change: stats.month_sales_change, format: formatCurrency, Icon: TrendUp, spark: sparkFromMonthly, testId: "kpi-monthly-sales" },
        { label: "Pending Orders", value: stats.pending_orders, Icon: ClipboardText, accent: "#F59E0B", testId: "kpi-pending-orders" },
        { label: "Completed Orders", value: stats.completed_orders, Icon: CheckCircle, testId: "kpi-completed-orders" },
        { label: "Total Customers", value: stats.total_customers, Icon: Users, testId: "kpi-customers" },
        { label: "Total Suppliers", value: stats.total_suppliers, Icon: Storefront, testId: "kpi-suppliers" },
        { label: "Total Profit (M)", value: stats.total_profit, format: formatCurrency, Icon: ChartLineUp, spark: sparkProfit, testId: "kpi-profit" },
        { label: "Purchase Value (M)", value: stats.total_purchase_value, format: formatCurrency, Icon: ShoppingBag, spark: sparkPurchase, testId: "kpi-purchase-value" },
    ];

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div>
                <div className="eyebrow mb-2">Overview</div>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Business snapshot</h1>
                <p className="text-sm text-neutral-500 mt-1.5">Refreshes automatically every 30 seconds.</p>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {kpis.map((k) => (<KpiCard key={k.label} {...k} />))}
            </div>

            {/* Payment strip */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="payment-strip">
                <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <PayTile label="Today's Collection" value={formatCurrency(payStats.today_collection)} sub={`${payStats.today_receipts} receipt${payStats.today_receipts !== 1 ? "s" : ""}`} Icon={Coins} accent="#15803D" testId="pay-today" />
                    <PayTile label="Monthly Collection" value={formatCurrency(payStats.month_collection)} sub={`${payStats.month_collection_change >= 0 ? "+" : ""}${payStats.month_collection_change}%`} Icon={TrendUp} accent="#15803D" testId="pay-month" />
                    <PayTile label="Pending" value={formatCurrency(payStats.pending_total)} sub="Across all customers" Icon={Wallet} accent="#F59E0B" testId="pay-pending" />
                    <PayTile label="Overdue" value={String(payStats.overdue_customers)} sub={formatCurrency(payStats.overdue_total)} Icon={WarningOctagon} accent="#DC2626" testId="pay-overdue" />
                </div>
                <div className="bg-white border border-neutral-200 rounded-md p-4 fade-up" data-testid="overdue-alerts">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="eyebrow">Alerts</div>
                            <h3 className="text-base font-bold font-display mt-1">Overdue Customers</h3>
                        </div>
                        <a href="/payments" className="text-xs font-semibold" style={{ color: "#15803D" }}>Open Payments →</a>
                    </div>
                    <div className="mt-3 space-y-2 max-h-[168px] overflow-y-auto">
                        {payStats.pending_customers.length === 0 ? (
                            <div className="text-xs text-neutral-400 py-4 text-center">No pending payments — all clear!</div>
                        ) : (
                            payStats.pending_customers.slice(0, 4).map((c) => (
                                <div key={c.id} className="flex items-center justify-between gap-2 border border-neutral-100 rounded p-2">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs font-bold truncate">{c.name}</div>
                                        <div className="text-[10px] text-neutral-500">Pending {formatCurrency(c.pending)}</div>
                                    </div>
                                    <a href={`https://wa.me/${(c.whatsapp || c.mobile || "").replace(/[^0-9]/g, "").replace(/^(\d{10})$/, "91$1")}?text=${encodeURIComponent(`Dear ${c.name}, gentle reminder — outstanding of ₹${c.pending} pending. — BS Fiber Product`)}`}
                                       target="_blank" rel="noreferrer"
                                       className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1"
                                       style={{ background: "#DCFCE7", color: "#15803D" }}
                                       data-testid={`overdue-remind-${c.name}`}>
                                        <WhatsappLogo size={10} weight="fill" /> Remind
                                    </a>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Charts row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <ChartCard title="Monthly Sales" testId="chart-monthly-sales">
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#15803D" stopOpacity={0.35} />
                                    <stop offset="95%" stopColor="#15803D" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                            <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
                            <Area type="monotone" dataKey="sales" stroke="#15803D" fillOpacity={1} fill="url(#salesFill)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Monthly Purchase" testId="chart-monthly-purchase">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                            <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
                            <Bar dataKey="purchase" fill="#0A0A0A" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Monthly Profit" testId="chart-monthly-profit">
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                            <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                            <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
                            <Line type="monotone" dataKey="profit" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            {/* Charts row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <ChartCard title="Category-wise Stock Value" testId="chart-category-stock">
                    {charts.category_stock.length === 0 ? (
                        <EmptyChartMsg text="No stock recorded yet" />
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={charts.category_stock} dataKey="value" nameKey="name" cx="50%" cy="50%"
                                     innerRadius={55} outerRadius={90} paddingAngle={2}>
                                    {charts.category_stock.map((_, i) => (
                                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
                <ChartCard title="Top Selling Products" testId="chart-top-products">
                    {charts.top_products.length === 0 ? (
                        <EmptyChartMsg text="No sales yet" />
                    ) : (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={charts.top_products} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} stroke="#525252" />
                                <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatCurrency(v)} />
                                <Bar dataKey="revenue" fill="#15803D" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
                <ChartCard title="Stock Movement (30d)" testId="chart-stock-movement">
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={charts.stock_movement}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9CA3AF" interval={4} />
                            <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                            <Tooltip contentStyle={tooltipStyle} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="in" stroke="#15803D" strokeWidth={2} dot={false} name="Stock In" />
                            <Line type="monotone" dataKey="out" stroke="#DC2626" strokeWidth={2} dot={false} name="Stock Out" />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            {/* Recent activity */}
            <div className="bg-white border border-neutral-200 rounded-md" data-testid="recent-activity">
                <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <div className="eyebrow">Recent Activity</div>
                        <h3 className="text-lg font-bold font-display mt-1">Stock movements</h3>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                            <tr>
                                <th className="text-left px-5 py-2.5 font-semibold">Product</th>
                                <th className="text-left px-5 py-2.5 font-semibold">Type</th>
                                <th className="text-right px-5 py-2.5 font-semibold">Qty</th>
                                <th className="text-left px-5 py-2.5 font-semibold">Reference</th>
                                <th className="text-left px-5 py-2.5 font-semibold">User</th>
                                <th className="text-left px-5 py-2.5 font-semibold">When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activity.length === 0 && (
                                <tr><td colSpan={6} className="text-center py-10 text-neutral-400">No stock activity yet.</td></tr>
                            )}
                            {activity.map((a) => (
                                <tr key={a.id} className="data-row border-t border-neutral-100">
                                    <td className="px-5 py-2.5 font-medium">{a.product_name}</td>
                                    <td className="px-5 py-2.5"><MovementBadge type={a.movement_type} /></td>
                                    <td className="px-5 py-2.5 text-right tabular font-semibold">{a.quantity > 0 ? "+" : ""}{a.quantity}</td>
                                    <td className="px-5 py-2.5 text-neutral-500">{a.reference_type}</td>
                                    <td className="px-5 py-2.5 text-neutral-500">{a.user_name}</td>
                                    <td className="px-5 py-2.5 text-neutral-500">{formatDate(a.timestamp)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const tooltipStyle = {
    background: "#ffffff",
    border: "1px solid #e5e5e5",
    borderRadius: 6,
    fontSize: 12,
    padding: "8px 10px",
};

function ChartCard({ title, children, testId }) {
    return (
        <div className="bg-white border border-neutral-200 rounded-md p-4 fade-up" data-testid={testId}>
            <div className="mb-2">
                <div className="eyebrow">Analytics</div>
                <h3 className="text-base font-bold font-display mt-1">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function EmptyChartMsg({ text }) {
    return <div className="h-[260px] flex items-center justify-center text-sm text-neutral-400">{text}</div>;
}

function MovementBadge({ type }) {
    const map = {
        in:  { bg: "#DCFCE7", fg: "#15803D", label: "Stock In" },
        out: { bg: "#FEE2E2", fg: "#DC2626", label: "Stock Out" },
        adjustment: { bg: "#FEF3C7", fg: "#B45309", label: "Adjustment" },
    };
    const s = map[type] || { bg: "#F3F4F6", fg: "#374151", label: type };
    return (
        <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
              style={{ background: s.bg, color: s.fg }}>
            {s.label}
        </span>
    );
}

function PayTile({ label, value, sub, Icon, accent, testId }) {
    return (
        <div className="kpi-card bg-white border border-neutral-200 rounded-md p-3 fade-up" data-testid={testId}>
            <div className="flex items-start justify-between mb-2">
                <div className="eyebrow leading-tight">{label}</div>
                <div className="w-7 h-7 rounded flex items-center justify-center" style={{ background: accent + "14", color: accent }}>
                    <Icon size={14} weight="bold" />
                </div>
            </div>
            <div className="text-xl font-extrabold font-display tabular tracking-tight" style={{ color: "#0A0A0A" }}>{value}</div>
            <div className="text-[10px] text-neutral-500 mt-0.5">{sub}</div>
        </div>
    );
}
